'use client';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useActivityInteraction } from '@/app/components/shared/Community';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { Activity, ActivityInteractionParams, WorkWithTask } from '@/app/types';
import { CommunityEnum } from '@/app/types';
import { cn, mapTypeToExploreTab, reportEvent } from '@/app/utils';
import { activitiesListWorks } from '../apis';
import { ActivityFeed } from './ActivityFeed';
import { SubmitButton } from './SubmitButton';

const EmptyList = memo(({ activity }: { activity: Activity }) => {
  const t = useTranslations();
  const { open: openPostsModal } = useGlobalModal('postsModal');

  return (
    <div className="inline-flex h-[416px] w-full flex-shrink-0 flex-col items-center justify-center gap-4 rounded-xl bg-[#242933] px-14">
      <div className="text-center text-white leading-7">{t('activity.worksTab.empty')}</div>
      <SubmitButton
        onClick={() =>
          openPostsModal({
            regularActivity: activity,
            isActivityLocked: true,
            showMediaLinkInput: true,
            hidePostTutorial: true,
            hidePostReference: true,
          })
        }
        activity={activity}
      />
    </div>
  );
});

EmptyList.displayName = 'EmptyList';

const TabButton = memo(({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
  <div
    className={cn(
      'flex h-7 min-w-[62px] cursor-pointer items-center justify-center rounded-lg px-3 text-[13px] leading-[140%] transition-colors',
      active ? 'bg-white font-medium text-black' : 'bg-white/[.10] text-white hover:bg-white/[.15]',
    )}
    onClick={onClick}
  >
    {label}
  </div>
));

TabButton.displayName = 'TabButton';

export const WORK_TABS = {
  ALL: 'all',
  AWARDED: 'awarded',
} as const;

interface PostWorksContainerProps {
  activity: Activity;
  isContentExpanded?: boolean;
}

export function PostWorksContainer({ activity, isContentExpanded }: PostWorksContainerProps) {
  const isAwarded = activity.phase === 'awarded';

  const t = useTranslations();
  const syncFeedDetailConfig = useStore((state) => state.syncFeedDetailConfig);

  const [activeTab, setActiveTab] = useState<(typeof WORK_TABS)[keyof typeof WORK_TABS]>(
    isAwarded ? WORK_TABS.AWARDED : WORK_TABS.ALL,
  );
  const [works, setWorks] = useState<WorkWithTask[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);

  const interactionPosition = `activity_feeds_${activeTab}`;

  /** 更新作品点赞状态 */
  const updateWorkVoteStatus = useCallback((id: string, voted: boolean) => {
    setWorks((prev) =>
      prev.map((item) => {
        if (item.work?.id !== id) return item;
        const currentLikeNum = Number(item.work.like_num) || 0;
        const newLikeNum = currentLikeNum + (voted ? 1 : -1);
        return {
          ...item,
          work: { ...item.work, liked: voted, like_num: String(Math.max(0, newLikeNum)) },
        };
      }),
    );
  }, []);

  const {
    vote: activityVote,
    unVote: activityUnVote,
    loading: isVoting,
  } = useActivityInteraction({
    interactionPosition,
    onInteractionStatusChange: ({ id, voted }) => updateWorkVoteStatus(id, voted),
  });

  const { run: getWorks, loading } = useFetch(
    (token?: string) =>
      activitiesListWorks({
        query: {
          activity_id: activity.id,
          awarded: activeTab === WORK_TABS.AWARDED,
          'pager.page_token': token,
          'pager.pagesz': 10,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
          return;
        }

        if (data) {
          data.works?.forEach((workData, idx) => {
            reportEvent('contestsubmission_show', {
              index: idx + works.length + 1,
              activity_id: activity.id || '',
              work_id: workData.work?.id || '',
            });
          });

          setWorks((prev) => [...prev, ...(data.works || [])]);
          setIsEmpty(data.works?.length === 0 && works.length === 0);
          setPageToken(data.next_page_token);
          setHasMore(!!data.next_page_token);
        }
      },
    },
  );

  const handleLoadMore = useCallback(() => {
    return new Promise<void>((resolve) => {
      getWorks(pageToken);
      resolve();
    });
  }, [getWorks, pageToken]);

  const getWorkObjType = (workType?: string): CommunityEnum => {
    const type = mapTypeToExploreTab(workType);
    return type === CommunityEnum.ImageAsset ? CommunityEnum.ImageAsset : CommunityEnum.MediaAsset;
  };

  const buildVoteParams = useCallback(
    (id: string): ActivityInteractionParams | null => {
      const work = works.find((item) => item.work?.id === id);
      if (!work?.work) return null;
      return {
        activityId: activity.id as string,
        id,
        obj_type: getWorkObjType(work.work.type),
        isInspiration: work.work.is_inspiration,
      };
    },
    [works, activity.id],
  );

  const handleVote = useCallback(
    (params: Omit<ActivityInteractionParams, 'obj_type'>) => {
      if (isVoting) return;
      const voteParams = buildVoteParams(params.id);
      if (!voteParams) return;

      if (params.voteType === 'dislike') {
        activityUnVote(voteParams);
      } else {
        activityVote(voteParams);
      }
    },
    [isVoting, buildVoteParams, activityVote, activityUnVote],
  );

  const handleVoteRef = useRef(handleVote);
  handleVoteRef.current = handleVote;

  const handleTabChange = useCallback(
    (tab: (typeof WORK_TABS)[keyof typeof WORK_TABS]) => {
      if (activeTab === tab) return;
      reportEvent('work_tab_switch', {
        activity_id: activity.id || '',
        selected_tab: tab,
      });
      setActiveTab(tab);
    },
    [activeTab, activity.id],
  );

  useEffect(() => {
    setWorks([]);
    setPageToken(undefined);
    setHasMore(true);
    setIsEmpty(false);
    getWorks(undefined);
  }, [activity.id, activeTab, getWorks]);

  useEffect(() => {
    syncFeedDetailConfig({
      source: 'inspiration',
      handleVote: (params) => {
        handleVoteRef.current({
          activityId: activity.id as string,
          id: params.id,
          voteType: 'like',
        });
      },
      handleUnVote: (params) => {
        handleVoteRef.current({
          activityId: activity.id as string,
          id: params.id,
          voteType: 'dislike',
        });
      },
      thumbScrollListConfig: {
        list: works,
        hasMore,
        onLoadMore: handleLoadMore,
      },
    });
  }, [works, hasMore, activity.id]);

  return (
    <>
      <div className="sticky top-[100px] z-10 flex w-full flex-col items-start gap-3 self-stretch border-white/10 border-t bg-[#020B13] py-4 md:top-[135px]">
        <h2 className="font-semibold text-white leading-[140%]">{t('activity.worksTab.title')}</h2>
        <div className="flex items-center gap-2 rounded-xl">
          {[WORK_TABS.ALL, WORK_TABS.AWARDED]
            .filter((tab) => isAwarded || tab === WORK_TABS.ALL) // 如果活动未结束，则只显示全部
            .map((tab) => (
              <TabButton
                key={tab}
                active={activeTab === tab}
                label={t(`activity.worksTab.select.${tab}`)}
                onClick={() => handleTabChange(tab)}
              />
            ))}
        </div>
      </div>

      <div className="w-full">
        {loading && works.length === 0 ? (
          <div className="relative h-[416px]">
            <GlobalLoader className="-translate-y-1/2 top-1/2" />
          </div>
        ) : isEmpty ? (
          <EmptyList activity={activity} />
        ) : (
          <ActivityFeed
            key={`activity-feed-${activeTab}-${isContentExpanded}`}
            works={works}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onVote={handleVote}
            onUnVote={handleVote}
            interactionPosition={interactionPosition}
            activityId={activity.id || ''}
            workTab={activeTab}
          />
        )}
      </div>
    </>
  );
}
